import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Wallet, Plus, Trash2, Loader2, Save, RotateCcw, Info, Calculator } from "lucide-react";

const eur = (n) =>
  n == null || isNaN(n)
    ? "—"
    : `€ ${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Pure list helpers + presentational components defined at MODULE level. Defining them
// inside the component made them a NEW type on every render, so React remounted the
// <input> after each keystroke and the cursor dropped out — the reported bug.
const setRow = (list, setList, i, key, val) =>
  setList(list.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
const addRow = (list, setList) => setList([...list, { label: "", amount: 0 }]);
const delRow = (list, setList, i) => setList(list.filter((_, idx) => idx !== i));

const CostTable = ({ title, rows, setRows, testid, accent, L }) => {
  const tot = rows.reduce((s, r) => s + (isNaN(parseFloat(r.amount)) ? 0 : parseFloat(r.amount)), 0);
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-border flex items-center justify-between"
        style={{ backgroundColor: `color-mix(in srgb, ${accent} 10%, transparent)` }}>
        <h3 className="font-bold uppercase tracking-wide text-sm" style={{ color: accent }}>{title}</h3>
        <span className="text-xs text-muted-foreground">{rows.length} {L("voci", "items")}</span>
      </div>
      <div className="divide-y divide-border/60">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2 hover:bg-accent/20" data-testid={`${testid}-row-${i}`}>
            <input
              data-testid={`${testid}-label-${i}`}
              value={r.label}
              onChange={(e) => setRow(rows, setRows, i, "label", e.target.value)}
              placeholder={L("Descrizione costo", "Cost description")}
              className="flex-1 h-9 bg-transparent text-sm px-2 rounded-md border border-transparent hover:border-border focus:border-primary focus:outline-none"
            />
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">€</span>
              <input
                data-testid={`${testid}-amount-${i}`}
                type="number" min="0" step="0.01"
                value={r.amount}
                onChange={(e) => setRow(rows, setRows, i, "amount", e.target.value)}
                className="w-28 h-9 text-right font-data text-sm px-2 rounded-md border border-border bg-background focus:border-primary focus:outline-none"
              />
            </div>
            <button data-testid={`${testid}-del-${i}`} onClick={() => delRow(rows, setRows, i)}
              className="p-1.5 text-muted-foreground hover:text-red-500 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-secondary/40">
        <button data-testid={`${testid}-add`} onClick={() => addRow(rows, setRows)}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
          <Plus className="w-3.5 h-3.5" />{L("Aggiungi voce", "Add item")}
        </button>
        <div className="text-right">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">TOTALE</span>
          <div className="font-data font-bold text-lg" data-testid={`${testid}-total`}>{eur(tot)}</div>
        </div>
      </div>
    </div>
  );
};

const CalcRow = ({ label, value, formula, testid, strong }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0" data-testid={testid}>
    <div className="min-w-0">
      <p className={`text-sm ${strong ? "font-semibold" : ""}`}>{label}</p>
      {formula && <p className="text-[11px] text-muted-foreground">{formula}</p>}
    </div>
    <span className={`font-data shrink-0 ml-3 ${strong ? "text-lg font-bold" : "font-semibold"}`}>{value}</span>
  </div>
);

export default function Costs() {
  const { lang, properties, activeId } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const active = properties.find((p) => p.id === activeId) || properties[0];

  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [fixed, setFixed] = useState([]);
  const [variable, setVariable] = useState([]);
  const [meta, setMeta] = useState({ rooms: 1, days_in_year: 365, rooms_available_auto: 365 });
  const [roomsAvailable, setRoomsAvailable] = useState(365);
  const [roomsSold, setRoomsSold] = useState(0);
  const [roomsSoldReal, setRoomsSoldReal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (yr) => {
    if (!active?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${active.id}/costs?year=${yr}`);
      setYears(data.available_years || []);
      setFixed(data.fixed_costs || []);
      setVariable(data.variable_costs || []);
      setMeta(data);
      setRoomsAvailable(data.rooms_available);
      setRoomsSold(data.rooms_sold); // resets to the real imported value on each load
      setRoomsSoldReal(data.rooms_sold_real);
    } catch {
      toast.error(L("Errore nel caricamento dei costi", "Error loading costs"));
    } finally {
      setLoading(false);
    }
  }, [active?.id, lang]);

  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [active?.id, year]);

  // --- live calculations (mirror backend formulas) ---
  const calc = useMemo(() => {
    const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));
    const totFixed = fixed.reduce((s, r) => s + num(r.amount), 0);
    const totVar = variable.reduce((s, r) => s + num(r.amount), 0);
    const ra = num(roomsAvailable);
    const rs = num(roomsSold);
    const fixedUnit = ra ? totFixed / ra : null;
    const fixedPerSold = rs ? totFixed / rs : null;
    const varPerSold = rs ? totVar / rs : null;
    const starting = rs ? (fixedPerSold || 0) + (varPerSold || 0) : null;
    return { totFixed, totVar, fixedUnit, fixedPerSold, varPerSold, starting };
  }, [fixed, variable, roomsAvailable, roomsSold]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        fixed_costs: fixed,
        variable_costs: variable,
        rooms_available_override:
          Number(roomsAvailable) !== Number(meta.rooms_available_auto) ? Number(roomsAvailable) : null,
      };
      const { data } = await api.put(`/properties/${active.id}/costs?year=${year}`, body);
      setFixed(data.fixed_costs || []);
      setVariable(data.variable_costs || []);
      setMeta(data);
      toast.success(L("Costi salvati", "Costs saved"));
    } catch {
      toast.error(L("Errore nel salvataggio", "Save error"));
    } finally {
      setSaving(false);
    }
  };

  if (!active?.id) {
    return (
      <div className="App min-h-screen"><Nav />
        <div className="max-w-6xl mx-auto px-6 py-24 text-center text-muted-foreground">
          {L("Crea prima una struttura.", "Create a property first.")}
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen"><Nav />
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="costs-page">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Wallet className="w-7 h-7 text-primary" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{L("Costi", "Costs")}</h1>
              <p className="text-sm text-muted-foreground">{active?.name} · {active?.city}</p>
            </div>
          </div>
          {/* Year selector */}
          <div className="flex items-center gap-1.5 bg-secondary/60 rounded-lg p-1" data-testid="costs-year-selector">
            {years.map((y) => (
              <button key={y} data-testid={`costs-year-${y}`} onClick={() => setYear(y)}
                className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${
                  y === year ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>
                {y}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-2 text-xs text-muted-foreground mb-6 bg-secondary/30 rounded-md px-3 py-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          {L("Le voci iniziali sono un esempio per capire quali costi vanno in ciascuna tabella. Modifica gli importi, aggiungi o elimina voci: la tariffa di partenza si ricalcola da sola. Questa pagina è puramente informativa e non modifica i prezzi consigliati.",
            "The initial items are an example to show which cost goes in each table. Edit amounts, add or remove items: the starting rate recalculates automatically. This page is purely informational and does not change the recommended prices.")}
        </div>

        {loading ? (
          <div className="py-24 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />{L("Caricamento…", "Loading…")}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <CostTable title={L("Costi Fissi", "Fixed Costs")} rows={fixed} setRows={setFixed} testid="fixed-costs" accent="#6366f1" L={L} />
              <CostTable title={L("Costi Variabili", "Variable Costs")} rows={variable} setRows={setVariable} testid="variable-costs" accent="#0ea5e9" L={L} />
            </div>

            <div className="flex justify-end mb-8">
              <Button data-testid="costs-save-btn" onClick={save} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {L("Salva costi", "Save costs")}
              </Button>
            </div>

            {/* Calculation panel */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6" data-testid="costs-calc">
              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Calculator className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">{L(`Calcolo tariffa ${year}`, `Rate calculation ${year}`)}</h3>
                </div>

                {/* editable inputs */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{L("Tot. camere disponibili", "Total rooms available")}</label>
                    <input data-testid="rooms-available-input" type="number" min="1" value={roomsAvailable}
                      onChange={(e) => setRoomsAvailable(e.target.value)}
                      className="mt-1 w-full h-10 font-data text-sm px-3 rounded-md border border-border bg-background focus:border-primary focus:outline-none" />
                    <p className="text-[10px] text-muted-foreground mt-0.5">{meta.rooms} {L("camere", "rooms")} × {meta.days_in_year} {L("giorni", "days")} = {meta.rooms_available_auto}</p>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{L("Tot. camere vendute", "Total rooms sold")}</label>
                    <input data-testid="rooms-sold-input" type="number" min="0" value={roomsSold}
                      onChange={(e) => setRoomsSold(e.target.value)}
                      className="mt-1 w-full h-10 font-data text-sm px-3 rounded-md border border-border bg-background focus:border-primary focus:outline-none" />
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-[10px] text-muted-foreground">{L("Reale da storico", "Real from history")}: {roomsSoldReal}</p>
                      {Number(roomsSold) !== Number(roomsSoldReal) && (
                        <button data-testid="rooms-sold-reset" onClick={() => setRoomsSold(roomsSoldReal)}
                          className="inline-flex items-center gap-0.5 text-[10px] text-primary hover:underline">
                          <RotateCcw className="w-3 h-3" />{L("ripristina", "reset")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <CalcRow testid="calc-fixed-unit" label={L("Costo fisso unitario", "Fixed cost per available room")}
                  formula={L("Tot. costi fissi ÷ tot. camere disponibili", "Total fixed ÷ rooms available")}
                  value={eur(calc.fixedUnit)} />
                <CalcRow testid="calc-fixed-per-sold" label={L("Costo fisso per camera venduta", "Fixed cost per sold room")}
                  formula={L("Tot. costi fissi ÷ tot. camere vendute", "Total fixed ÷ rooms sold")}
                  value={eur(calc.fixedPerSold)} />
                <CalcRow testid="calc-var-per-sold" label={L("Costo variabile per camera venduta", "Variable cost per sold room")}
                  formula={L("Tot. costi variabili ÷ tot. camere vendute", "Total variable ÷ rooms sold")}
                  value={eur(calc.varPerSold)} />
              </div>

              {/* Starting rate box */}
              <div className="bg-primary text-primary-foreground rounded-lg p-6 flex flex-col justify-center" data-testid="starting-rate-box">
                <p className="text-xs uppercase tracking-widest font-bold opacity-80">{L("Tariffa di partenza", "Starting rate")}</p>
                <p className="font-data text-5xl font-bold mt-2" data-testid="starting-rate-value">{eur(calc.starting)}</p>
                <p className="text-xs opacity-80 mt-3">
                  {L("Costo fisso per camera venduta + costo variabile per camera venduta. È il prezzo sotto cui vendere significa perdere.",
                    "Fixed + variable cost per sold room. Selling below this means losing money.")}
                </p>
                {!calc.starting && (
                  <p className="text-xs opacity-90 mt-3 bg-white/15 rounded-md px-3 py-2">
                    {L("Inserisci i costi e le camere vendute per calcolare la tariffa.", "Enter costs and rooms sold to compute the rate.")}
                  </p>
                )}
              </div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
